/**
 * SealRequestListPage — Issue #365.
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_AFFAIRS_PERMISSIONS,
  hasPermission,
} from '@/lib/adminAffairsPermissions';
import {
  ADMIN_AFFAIR_REQUEST_STATUS_LABELS,
  ADMIN_SEAL_TYPE_LABELS,
  useAdminSealRequestMutations,
  useAdminSealRequests,
  type AdminSealRequestDto,
  type CreateAdminSealRequestDto,
} from '@/hooks/useAdminAffairs';

const STATUS_BADGE: Readonly<Record<AdminSealRequestDto['status'], string>> = {
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

export default function SealRequestListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, ADMIN_AFFAIRS_PERMISSIONS.READ);
  const canWrite = hasPermission(permissions, ADMIN_AFFAIRS_PERMISSIONS.WRITE);
  const canApprove = hasPermission(
    permissions,
    ADMIN_AFFAIRS_PERMISSIONS.APPROVE,
  );

  const { items, total, loading, error, fetchSealRequests } =
    useAdminSealRequests();
  const {
    loading: actionLoading,
    error: actionError,
    create,
    action,
  } = useAdminSealRequestMutations();

  const [statusFilter, setStatusFilter] = useState<
    AdminSealRequestDto['status'] | ''
  >('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateAdminSealRequestDto>({
    sealType: 'company',
    documentTitle: '',
    purpose: '',
  });

  useEffect(() => {
    if (!canRead) return;
    void fetchSealRequests({ status: statusFilter || undefined });
  }, [canRead, statusFilter, fetchSealRequests]);

  if (!canRead) return <AccessDenied resource="用印申请" />;

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">用印申请</h1>
          <p className="text-sm text-slate-500">
            按用印类型审批；附件仅保存 file id，正文 / URL 通过短期签名链路访问。
          </p>
        </div>
        {canWrite ? (
          <button
            type="button"
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            data-testid="seal-create-entry"
            onClick={() => setCreating((v) => !v)}
          >
            {creating ? '取消' : '新增用印申请 →'}
          </button>
        ) : null}
      </header>

      {creating && canWrite ? (
        <Card className="space-y-3 p-4">
          <label className="block text-sm">
            用印类型
            <select
              className="mt-1 block w-48 rounded border border-slate-300 px-2 py-1"
              value={form.sealType}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  sealType: e.target.value as CreateAdminSealRequestDto['sealType'],
                }))
              }
              data-testid="seal-create-type"
            >
              {(
                Object.entries(ADMIN_SEAL_TYPE_LABELS) as [
                  AdminSealRequestDto['sealType'],
                  string,
                ][]
              ).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            文件名称
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.documentTitle}
              onChange={(e) =>
                setForm((f) => ({ ...f, documentTitle: e.target.value }))
              }
              data-testid="seal-create-title"
            />
          </label>
          <label className="block text-sm">
            用印事由
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.purpose}
              onChange={(e) =>
                setForm((f) => ({ ...f, purpose: e.target.value }))
              }
              data-testid="seal-create-purpose"
            />
          </label>
          <label className="block text-sm">
            附件 file id（可选）
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
              value={form.attachmentFileId ?? ''}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  attachmentFileId: e.target.value || undefined,
                }))
              }
            />
          </label>
          <button
            type="button"
            disabled={actionLoading}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
            data-testid="seal-create-submit"
            onClick={async () => {
              const created = await create(form);
              if (created) {
                setForm({
                  sealType: 'company',
                  documentTitle: '',
                  purpose: '',
                });
                setCreating(false);
                await fetchSealRequests({ status: statusFilter || undefined });
              }
            }}
          >
            {actionLoading ? '提交中…' : '创建草稿'}
          </button>
          {actionError ? (
            <p className="text-sm text-red-600" data-testid="seal-create-error">
              {actionError.message}
            </p>
          ) : null}
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                (e.target.value as AdminSealRequestDto['status']) || '',
              )
            }
            data-testid="seal-status-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_AFFAIR_REQUEST_STATUS_LABELS) as [
                AdminSealRequestDto['status'],
                string,
              ][]
            ).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">编号</th>
              <th className="px-3 py-2">用印类型</th>
              <th className="px-3 py-2">文件</th>
              <th className="px-3 py-2">状态</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={5}
                  data-testid="seal-loading"
                >
                  加载中…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={5}
                  data-testid="seal-empty"
                >
                  暂无用印申请
                </td>
              </tr>
            ) : (
              items.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">
                    {s.sealRequestNo}
                  </td>
                  <td className="px-3 py-2">
                    {ADMIN_SEAL_TYPE_LABELS[s.sealType]}
                  </td>
                  <td className="px-3 py-2">{s.documentTitle}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_BADGE[s.status]}`}
                    >
                      {ADMIN_AFFAIR_REQUEST_STATUS_LABELS[s.status]}
                    </span>
                  </td>
                  <td className="space-x-2 px-3 py-2 text-xs">
                    {s.status === 'draft' && canWrite ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        data-testid={`seal-submit-${s.id}`}
                        onClick={async () => {
                          await action(s.id, 'submit', s.version);
                          await fetchSealRequests({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        提交审批
                      </button>
                    ) : null}
                    {s.status === 'submitted' && canApprove ? (
                      <>
                        <button
                          type="button"
                          className="text-emerald-600 hover:underline"
                          data-testid={`seal-approve-${s.id}`}
                          onClick={async () => {
                            await action(s.id, 'approve', s.version);
                            await fetchSealRequests({
                              status: statusFilter || undefined,
                            });
                          }}
                        >
                          批准
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:underline"
                          data-testid={`seal-reject-${s.id}`}
                          onClick={async () => {
                            const reason = window.prompt('拒绝原因');
                            if (!reason) return;
                            await action(s.id, 'reject', s.version, reason);
                            await fetchSealRequests({
                              status: statusFilter || undefined,
                            });
                          }}
                        >
                          拒绝
                        </button>
                      </>
                    ) : null}
                    {s.status === 'approved' && canApprove ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        data-testid={`seal-complete-${s.id}`}
                        onClick={async () => {
                          await action(s.id, 'complete', s.version);
                          await fetchSealRequests({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        用印完成
                      </button>
                    ) : null}
                    {(s.status === 'draft' ||
                      s.status === 'submitted' ||
                      s.status === 'approved') &&
                    canWrite ? (
                      <button
                        type="button"
                        className="text-slate-600 hover:underline"
                        data-testid={`seal-cancel-${s.id}`}
                        onClick={async () => {
                          const reason = window.prompt('取消原因（可选）');
                          await action(
                            s.id,
                            'cancel',
                            s.version,
                            reason ?? undefined,
                          );
                          await fetchSealRequests({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        取消
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-slate-500">合计 {total} 条用印申请</p>
    </div>
  );
}
